import React, { useState, useCallback, useEffect } from 'react';
import graphql from 'src/utils/graphql';
import { Table, Alert } from 'reactstrap';
import Spinner from 'src/components/Spinner';
import { AskFile, AskSelect, AskText, AskDate } from 'src/components/AskComponents';
import { useApp } from 'src/components/App/appContext';
import LoginFallback from './loginFallback';
import moment from 'moment';

export default function Profile() {
	const [profile, setProfile] = useState(null);
	const { user, fetchUser } = useApp();

	const fetchProfile = useCallback(async () => {
		let { me } = await graphql(
			{
				query: `{
						me {
							id
							name
							firstName
							lastName
							email
							weight
							height
							personHeadSize
							emergencyContactName
							emergencyContactMobile
							dob
							avatarObject {
								url
							}
							gender
							mobile
							profileComplete
							emailMarketing
							bhpaNumber
							bhpaExpiry
							participations(orderBy: {date: DESC}) {
								id
								status {
									name
								}
								date
								activity {
									id
									name
								}
							}
						}
					}
				`
			},
			true
		);
		me = me[0];

		setProfile(me);
	}, []);

	const updateProfile = useCallback(
		async (values) => {
			await graphql({
				query: `mutation ($input: PersonInput!) {
				savePerson(input: $input)
			}`,
				variables: {
					input: {
						...values,
						id: profile.id
					}
				}
			});
			await fetchProfile();
			await fetchUser(); //Refresh me
		},
		[fetchProfile, profile]
	);

	useEffect(() => {
		if (!user) return setProfile(null);
		fetchProfile();
	}, [fetchProfile, user]);

	if (!profile && !user) return <LoginFallback />;

	if (!profile || !user) return <Spinner />;

	return (
		<div>
			<div className="row">
				<div className="col-md-6">				

					{/* Profile Status Alert */}
					{profile.profileComplete ? (
						<Alert style={{ 
							padding: '16px', 
							borderRadius: '12px',
							border: 'none',
							backgroundColor: '#d4edda',
							color: '#155724'
						}} color="success">
							<i className="fa fa-check-circle" style={{ marginRight: '8px' }}></i>
							<strong>Great job!</strong> Your profile is complete. You can edit your profile details below.
						</Alert>
					) : (
						<Alert style={{ 
							padding: '16px', 
							borderRadius: '12px',
							border: 'none',
							backgroundColor: '#fff3cd',
							color: '#856404'
						}} color="warning">
							<i className="fa fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
							<strong>Profile Incomplete:</strong> Please complete your profile below to get the most out of your account.
						</Alert>
					)}
					<h4 style={{
						margin: '32px 0 16px 0',
						color: '#495057',
						fontSize: '20px',
						fontWeight: '600',
						display: 'flex',
						alignItems: 'center',
						gap: '12px'
					}}>
						<i className="fa fa-user-edit" style={{ color: '#667eea' }}></i>
						Profile Details
					</h4>
					<Table bordered style={{
						borderRadius: '12px',
						overflow: 'hidden',
						boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
						border: '1px solid #e9ecef'
					}}>
						<tbody>
							<TableRow label="Avatar">
								<div style={{ 
									display: 'flex', 
									flexDirection: 'column', 
									alignItems: 'center', 
									gap: '16px',
									padding: '20px',
									backgroundColor: '#f8f9fa',
									borderRadius: '12px',
									border: '2px dashed #dee2e6'
								}}>
									<div style={{ 
										position: 'relative',
										display: 'inline-block'
									}}>
										<img 
											src={profile?.avatarObject?.url || '/no-image.png'} 
											style={{ 
												width: '120px', 
												height: '120px',
												borderRadius: '50%',
												objectFit: 'cover',
												border: '4px solid #fff',
												boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
											}} 
											alt="Profile Avatar"
										/>
										{profile?.avatarObject?.url && (
											<div style={{
												position: 'absolute',
												bottom: '0',
												right: '0',
												backgroundColor: '#28a745',
												color: 'white',
												borderRadius: '50%',
												width: '32px',
												height: '32px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												border: '3px solid #fff',
												boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
											}}>
												<i className="fa fa-check" style={{ fontSize: '14px' }}></i>
											</div>
										)}
									</div>
									<div style={{ textAlign: 'center' }}>
										<h5 style={{ margin: '0 0 8px 0', color: '#495057' }}>
											{profile?.avatarObject?.url ? 'Current Avatar' : 'No Avatar Set'}
										</h5>
										<p style={{ 
											margin: '0 0 16px 0', 
											color: '#6c757d', 
											fontSize: '14px' 
										}}>
											{profile?.avatarObject?.url 
												? 'Click below to change your profile picture' 
												: 'Upload a profile picture to personalize your account'
											}
										</p>
										<AskFile 
											title={profile?.avatarObject?.url ? "Change Avatar" : "Upload Avatar"} 
											onSubmit={(v) => updateProfile({ avatar: v.id })}
										/>
									</div>
								</div>
							</TableRow>
							<TableRow label="Fist Name">
								{profile.firstName}&nbsp;
								<AskText
									title="First Name"
									value={profile.firstName}
									onSubmit={(v) => updateProfile({ firstName: v })}
								/>
							</TableRow>
							<TableRow label="Last Name">
								{profile.lastName}&nbsp;
								<AskText title="Last Name" value={profile.lastName} onSubmit={(v) => updateProfile({ lastName: v })} />
							</TableRow>
							<TableRow label="Email">
								{profile.email}&nbsp;
								{/* <AskText title="Email" value={profile.email} onSubmit={(v) => updateProfile({ email: v })} /> */}
							</TableRow>
							<TableRow label="Mobile">
								{profile.mobile}&nbsp;
								<AskText title="Mobile" value={profile.mobile} onSubmit={(v) => updateProfile({ mobile: v })} />
							</TableRow>
							<TableRow label="Height">
								{profile.height}&nbsp;
								<AskText title="Height" value={profile.height} onSubmit={(v) => updateProfile({ height: v })} />
							</TableRow>
							<TableRow label="Weight">
								{profile.weight}&nbsp;
								<AskText title="Weight" value={profile.weight} onSubmit={(v) => updateProfile({ weight: v })} />
							</TableRow>
							<TableRow label="Head Size (cm)">
								{profile.personHeadSize ? `${profile.personHeadSize} cm` : ''}&nbsp;
								<AskText
									title="Head Size (cm)"
									description="Head circumference in centimetres (e.g. 56.5)"
									value={profile.personHeadSize}
									onSubmit={(v) => {
										const n = parseFloat(v);
										if (isNaN(n)) return 'Please enter a number';
										if (n < 40 || n > 80) return 'Please enter a value between 40 and 80 cm';
										if (!/^\d{1,3}(\.\d{1,2})?$/.test(v.trim())) return 'Max two decimal places';
										return updateProfile({ headSize: String(n) });
									}}
								/>
							</TableRow>
							<TableRow label="Emergency Contact Name">
								{profile.emergencyContactName || ''}&nbsp;
								<AskText
									title="Emergency Contact Name"
									value={profile.emergencyContactName}
									onSubmit={(v) => updateProfile({ emergencyContactName: v })}
								/>
							</TableRow>
							<TableRow label="Emergency Contact Number">
								{profile.emergencyContactMobile || ''}&nbsp;
								<AskText
									title="Emergency Contact Number"
									value={profile.emergencyContactMobile}
									onSubmit={(v) => updateProfile({ emergencyContactMobile: v })}
								/>
							</TableRow>
							<TableRow label="Date of Birth">
								{new Date(profile.dob).toLocaleDateString('en-GB')}&nbsp;
								<AskDate title="Date of Birth" value={profile.dob} onSubmit={(v) => updateProfile({ dob: v })} />
							</TableRow>
							<TableRow label="Gender">
								{profile.gender ? 'Male' : 'Female'}&nbsp;
								<AskSelect
									title="Gender"
									value={profile.gender}
									values={[
										{
											text: 'Male',
											value: true
										},
										{
											text: 'Female',
											value: false
										}
									]}
									onSubmit={(v) => updateProfile({ gender: v === 'true' })}
								/>
							</TableRow>
							<TableRow label="Communication Settings">
								{profile.emailMarketing ? 'Yes' : 'No'}&nbsp;
								<AskSelect
									title="Communication Settings"
									description="Would you like to receive communication & offers from us?"
									value={profile.emailMarketing}
									values={[
										{
											text: 'Yes',
											value: true
										},
										{
											text: 'No',
											value: false
										}
									]}
									onSubmit={(v) => updateProfile({ emailMarketing: v === 'true' })}
								/>
							</TableRow>
						</tbody>
					</Table>

					<h4 style={{
						margin: '32px 0 16px 0',
						color: '#495057',
						fontSize: '20px',
						fontWeight: '600',
						display: 'flex',
						alignItems: 'center',
						gap: '12px'
					}}>
						<i className="fa fa-id-card" style={{ color: '#28a745' }}></i>
						BHPA Information
					</h4>
					<Table bordered style={{
						borderRadius: '12px',
						overflow: 'hidden',
						boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
						border: '1px solid #e9ecef'
					}}>
						<tbody>
							<TableRow label="BHPA Number">
								{profile.bhpaNumber || 'None'}&nbsp;
								<AskText
									title="BHPA Number"
									value={profile.bhpaNumber}
									onSubmit={(v) => updateProfile({ bhpaNumber: v })}
								/>
							</TableRow>
							<TableRow label="BHPA Expiry">
								{profile.bhpaExpiry && moment(profile.bhpaExpiry).isBefore(moment()) && (
									<Alert color="danger">Your BHPA has expired!</Alert>
								)}
								{profile.bhpaExpiry ? new Date(profile.bhpaExpiry).toLocaleDateString('en-GB') : 'None'}&nbsp;
								<AskDate
									title="BHPA Expiry"
									value={profile.bhpaExpiry}
									// check date valid if not use null
									onSubmit={(v) => {
										if (new Date(v).toString() === 'Invalid Date') {
											return updateProfile({ bhpaExpiry: null });
										}
										updateProfile({ bhpaExpiry: v });
									}}
								/>
							</TableRow>
						</tbody>
					</Table>
				</div>
				<div className="col-md-6">
					<h3>My Flights</h3>
					<Table bordered>
						<tbody>
							{profile.participations.map((p) => (
								<tr key={p.id}>
									<td>{p.activity.name}</td>
									<td>{p.date}</td>
									<td>{p.status.name}</td>
								</tr>
							))}
						</tbody>
					</Table>
				</div>
			</div>
		</div>
	);
}

const TableRow = ({ label, children }) => (
	<tr>
		<td>{label}</td>
		<td>{children}</td>
	</tr>
);
